import { useAppDispatch } from '@/app/redux/hooks';
import { useStudents } from '@/app/redux/hooks/students';
import { setStudentCreationModalOpen } from '@/app/redux/slices/modalsSlice';
import {
  CustomLevel,
  UpdateCustomLevelInput,
} from '@/common/types/customLevel';
import { DashboardViewContent } from '@/common/ui/DashboardViewStyles';
import { GAMES, mapActivityToSkill } from '@/common/utils/helpers';
import {
  allGraphemes,
  allSentences,
  allWords,
  getWordById,
  isOneEditDistance,
  isOneMorphemeEditDistance,
} from '@/common/utils/scopeAndSequence';
import { LearnerMultiSelect } from '@/dashboard/MultiSelect';
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckCircleIcon,
  DotsSixVerticalIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Checkbox } from '@shadcn/components/checkbox';
import { Label } from '@shadcn/components/label';
import { cn } from '@shadcn/lib/utils';
import { useMemo, useState } from 'react';
import ItemSelector from './ItemSelector';

export type GameId = (typeof GAMES)[number];

export const BLANK_GAME_DATA = { enabled: false, items: [] };

export const BLANK_LEVEL: Omit<
  CustomLevel,
  'id' | 'createdBy' | 'organisationId' | 'createdAt' | 'updatedAt'
> = {
  description: '',
  students: {},
  mysticMatch: BLANK_GAME_DATA,
  wordWeaver: BLANK_GAME_DATA,
  phonemeForge: BLANK_GAME_DATA,
  sentenceBuilder: BLANK_GAME_DATA,
  wordSums: BLANK_GAME_DATA,
  morphMaster: BLANK_GAME_DATA,
};

type ChainValidations = Record<
  string,
  { reason: string; severity: 'warning' | 'error' }
>;

function getGameSummary(
  gameId: GameId,
  level: CustomLevel,
  wordChainHasInvalid: boolean,
  morphemeChainHasInvalid: boolean,
): { severity: 'warning' | 'error' | 'info'; summary: string } {
  const gameData = level[gameId];
  const enabled = gameData.enabled;
  const itemCount = gameData.items.length;
  let itemType: string;
  switch (gameId) {
    case 'mysticMatch':
      itemType = 'grapheme';
      break;
    case 'wordWeaver':
    case 'wordSums':
    case 'phonemeForge':
    case 'morphMaster':
      itemType = 'word';
      break;
    case 'sentenceBuilder':
      itemType = 'sentence';
      break;
  }

  if (gameId === 'phonemeForge' && wordChainHasInvalid) {
    return {
      severity: 'warning',
      summary: 'Some consecutive words differ by more than one grapheme',
    };
  }

  if (gameId === 'morphMaster' && morphemeChainHasInvalid) {
    return {
      severity: 'warning',
      summary: 'Some consecutive words differ by more than one morpheme',
    };
  }

  return itemCount > 0
    ? {
        severity: 'info',
        summary: `${itemCount} ${itemType}${itemCount === 1 ? '' : 's'}`,
      }
    : {
        severity: enabled ? 'error' : 'info',
        summary: `No ${itemType}${itemCount === 1 ? '' : 's'} selected`,
      };
}

interface GameInstruction {
  description: string;
  details: { label: string; value: string }[];
}

const gameInstructions: Record<GameId, GameInstruction> = {
  mysticMatch: {
    description:
      'Learners hear an audio cue and select the matching grapheme. Each grapheme must be identified correctly twice to complete the game.',
    details: [
      { label: 'Order', value: 'Random' },
      { label: 'Recommended', value: '4-6 graphemes' },
    ],
  },
  wordWeaver: {
    description:
      'Learners hear a spoken word and spell it by selecting the correct graphemes in order. Incorrect attempts receive feedback before retrying.',
    details: [
      { label: 'Order', value: 'Random' },
      { label: 'Recommended', value: '5-10 words' },
    ],
  },
  wordSums: {
    description:
      'Learners are given morpheme cards and must arrange them to build a target word. Incorrect attempts receive feedback before retrying.',
    details: [
      { label: 'Order', value: 'Random' },
      { label: 'Recommended', value: '5-10 words' },
    ],
  },
  phonemeForge: {
    description:
      'Learners work through a word chain, transforming each word into the next by swapping, adding, or removing a single grapheme. Each consecutive pair should differ by exactly one grapheme.',
    details: [
      { label: 'Order', value: 'Sequential' },
      { label: 'Recommended', value: '5-10 words' },
    ],
  },
  morphMaster: {
    description:
      'Learners work through a word chain, transforming each word into the next by swapping, adding, or removing a single morpheme. Each consecutive pair should differ by exactly one morpheme.',
    details: [
      { label: 'Order', value: 'Sequential' },
      { label: 'Recommended', value: '5-10 words' },
    ],
  },
  sentenceBuilder: {
    description:
      'Learners hear a sentence and reconstruct it by selecting its words in the correct order. Incorrect attempts receive feedback before retrying.',
    details: [
      { label: 'Order', value: 'Random' },
      { label: 'Recommended', value: '3-5 sentences' },
    ],
  },
};

interface CustomLevelEditorBodyProps {
  level: CustomLevel;
  onChange: (updates: UpdateCustomLevelInput) => void;
  assignedStudentIds: string[];
  onSelectStudent: (studentId: string) => void;
  onToggleGame: (game: GameId, enabled: boolean) => void;
}

export default function CustomLevelEditor({
  level,
  onChange,
  assignedStudentIds,
  onSelectStudent,
  onToggleGame,
}: CustomLevelEditorBodyProps) {
  const dispatch = useAppDispatch();
  const { students } = useStudents();
  const [rowOrder, setRowOrder] = useState<GameId[]>([...GAMES]);
  const sensors = useSensors(useSensor(MouseSensor));

  const games = useMemo(
    () => new Set(GAMES.filter((g) => level[g].enabled)),
    [level],
  );

  const wordChainValidations = useMemo(() => {
    const result: ChainValidations = {};
    level.phonemeForge.items.forEach((itemId, idx) => {
      const current = getWordById(itemId);
      if (!current) {
        result[itemId] = {
          reason: 'Word not found in word list',
          severity: 'error',
        };
        return;
      }
      if (idx === 0) return;
      const prev = getWordById(level.phonemeForge.items[idx - 1]);
      if (prev && !isOneEditDistance(prev, current)) {
        result[itemId] = {
          reason: 'More than one grapheme apart from the previous word',
          severity: 'warning',
        };
      }
    });
    return result;
  }, [level.phonemeForge.items]);

  const morphChainValidations = useMemo(() => {
    const result: ChainValidations = {};
    level.morphMaster.items.forEach((itemId, idx) => {
      const current = getWordById(itemId);
      if (!current) {
        result[itemId] = {
          reason: 'Word not found in word list',
          severity: 'error',
        };
        return;
      }
      if (idx === 0) return;
      const prev = getWordById(level.morphMaster.items[idx - 1]);
      if (prev && !isOneMorphemeEditDistance(prev, current)) {
        result[itemId] = {
          reason: 'More than one morpheme apart from the previous word',
          severity: 'warning',
        };
      }
    });
    return result;
  }, [level.morphMaster.items]);

  function handleDragEnd({ active, over }: DragEndEvent) {
    const overId = over?.id;
    if (!overId || active.id === overId) return;
    setRowOrder((prev) => {
      const activeIdx = prev.indexOf(active.id as GameId);
      const overIdx = prev.indexOf(overId as GameId);
      return arrayMove(prev, activeIdx, overIdx);
    });
  }

  return (
    <DashboardViewContent>
      <div className="m-6 space-y-2">
        <div id="custom-level-learners" className="border-b pb-6 mb-6">
          <h3 className="text-sm font-medium mb-3">Learners</h3>
          <LearnerMultiSelect
            items={students}
            selectedIds={assignedStudentIds}
            onSelect={onSelectStudent}
            onFilter={(term) =>
              students.filter((s) =>
                `${s.studentFirstName} ${s.studentSurname} ${s.spellcastUsername}`
                  .toLowerCase()
                  .includes(term.toLowerCase()),
              )
            }
            getItemDisabled={(s) =>
              !!s.customLevelId && s.customLevelId !== level.id
            }
            getDisabledReason={(s) =>
              `${s.studentFirstName} ${s.studentSurname} is already assigned to a custom level`
            }
            onAddItem={() => dispatch(setStudentCreationModalOpen(true))}
          />
        </div>

        <h3 className="text-sm font-medium">Games</h3>

        <div id="custom-level-games" className="space-y-4">
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
              items={rowOrder}
              strategy={verticalListSortingStrategy}
            >
              {rowOrder.map((gameId) => (
                <GameRow
                  key={gameId}
                  gameId={gameId}
                  level={level}
                  games={games}
                  wordChainValidations={wordChainValidations}
                  morphChainValidations={morphChainValidations}
                  onToggle={onToggleGame}
                  onChange={onChange}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </DashboardViewContent>
  );
}

interface GameRowProps {
  gameId: GameId;
  level: CustomLevel;
  games: Set<string>;
  wordChainValidations: ChainValidations;
  morphChainValidations: ChainValidations;
  onToggle: (game: GameId, enabled: boolean) => void;
  onChange: (updates: UpdateCustomLevelInput) => void;
}

function GameRow({
  gameId,
  level,
  games,
  wordChainValidations,
  morphChainValidations,
  onToggle,
  onChange,
}: GameRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: gameId });

  const enabled = games.has(gameId);
  const { severity, summary } = getGameSummary(
    gameId,
    level,
    Object.keys(wordChainValidations).length > 0,
    Object.keys(morphChainValidations).length > 0,
  );
  const instructions = gameInstructions[gameId];

  return (
    <div
      id={`game-row-${gameId}`}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="rounded-lg border bg-card"
    >
      <div
        className={`flex items-center gap-2 px-3 py-3 select-none ${!enabled ? 'opacity-60' : ''}`}
      >
        <button
          className="flex items-center text-muted-foreground hover:text-foreground focus:outline-none cursor-grab shrink-0"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <DotsSixVerticalIcon size={18} />
        </button>

        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <Checkbox
            id={`game-${gameId}`}
            checked={enabled}
            onCheckedChange={(checked) => onToggle(gameId, checked === true)}
          />
        </div>

        <Label
          htmlFor={`game-${gameId}`}
          className="font-semibold cursor-pointer shrink-0 flex items-baseline gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          {mapActivityToSkill(gameId).CleanName}
          <span className="text-xs text-muted-foreground">
            {mapActivityToSkill(gameId).Skill}
          </span>
        </Label>

        <span
          className={cn(
            'text-xs text-muted-foreground ml-auto shrink-0',
            severity === 'error' && 'text-destructive',
            severity === 'warning' && 'text-amber-500',
          )}
        >
          {summary}
        </span>

        {severity === 'error' ? (
          <WarningCircleIcon size={16} className="text-destructive" />
        ) : severity === 'warning' ? (
          <WarningCircleIcon size={16} className="text-amber-500" />
        ) : (
          <CheckCircleIcon size={16} className="text-green-500" />
        )}
      </div>

      <div
        className={`border-t px-4 pb-4 pt-3 flex flex-col gap-2 ${!enabled ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <div className="flex flex-col gap-1.5">
          <p className="text-sm text-muted-foreground">
            {instructions.description}
          </p>
          <div className="flex gap-2">
            {instructions.details.map(({ label, value }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1 text-xs bg-muted rounded px-1.5 py-0.5"
              >
                <span className="text-muted-foreground">{label}:</span>
                <span className="font-medium">{value}</span>
              </span>
            ))}
          </div>
        </div>

        {gameId === 'mysticMatch' && (
          <ItemSelector
            id="grapheme-selector"
            type="grapheme"
            items={allGraphemes}
            selectedItemIds={level.mysticMatch.items}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                mysticMatch: {
                  ...level.mysticMatch,
                  items: ids as typeof level.mysticMatch.items,
                },
              })
            }
          />
        )}

        {gameId === 'wordWeaver' && (
          <ItemSelector
            id="word-weaver-selector"
            type="word"
            items={allWords}
            selectedItemIds={level.wordWeaver.items}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                wordWeaver: {
                  ...level.wordWeaver,
                  items: ids as typeof level.wordWeaver.items,
                },
              })
            }
          />
        )}

        {gameId === 'wordSums' && (
          <ItemSelector
            id="word-sums-selector"
            type="word"
            showMorphemes
            items={allWords}
            selectedItemIds={level.wordSums.items}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                wordSums: {
                  ...level.wordSums,
                  items: ids as typeof level.wordSums.items,
                },
              })
            }
          />
        )}

        {gameId === 'phonemeForge' && (
          <ItemSelector
            id="word-chain-selector"
            type="word"
            items={allWords}
            selectedItemIds={level.phonemeForge.items}
            itemValidations={wordChainValidations}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                phonemeForge: {
                  ...level.phonemeForge,
                  items: ids as typeof level.phonemeForge.items,
                },
              })
            }
          />
        )}

        {gameId === 'morphMaster' && (
          <ItemSelector
            id="morpheme-chain-selector"
            type="word"
            showMorphemes
            items={allWords}
            selectedItemIds={level.morphMaster.items}
            itemValidations={morphChainValidations}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                morphMaster: {
                  ...level.morphMaster,
                  items: ids as typeof level.morphMaster.items,
                },
              })
            }
          />
        )}

        {gameId === 'sentenceBuilder' && (
          <ItemSelector
            id="sentence-selector"
            type="sentence"
            items={allSentences}
            selectedItemIds={level.sentenceBuilder.items}
            onSelectedItemIdsChange={(ids) =>
              onChange({
                sentenceBuilder: {
                  ...level.sentenceBuilder,
                  items: ids as typeof level.sentenceBuilder.items,
                },
              })
            }
          />
        )}
      </div>
    </div>
  );
}
